import { useState } from 'react';

import { archiveMerchant } from 'config/requests/merchants';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { Button } from 'shared/mui/Buttons';
import { BaseDialog } from 'shared/mui/Dialogs';
import { Typography, TypographyWithBold } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { Merchant } from 'types/embedded/merchant';

type MerchantsUnarchiveDialogProps = {
  record: Merchant;
  open: boolean;
  onClose: VoidFunction;
};

export const MerchantsUnarchiveDialog: React.FC<MerchantsUnarchiveDialogProps> = ({
  record,
  open,
  onClose,
}) => {
  const translate = useTypedTranslate();

  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotify();

  const onArchive = async () => {
    try {
      setIsLoading(true);
      await archiveMerchant(record.id, false);
      setIsLoading(false);
      notify(
        <TypographyWithBold
          text={translate('app.merchants.merchantRestoried', { name: record.name })}
        />,
        { type: 'success' },
      );
      onClose();
      await queryClient.refetchQueries({ queryKey: ['merchants'] });
    } catch (e: any) {
      notify(e?.data?.detail || translate('app.common.serverError'), { type: 'error' });
      setIsLoading(false);
    }
  };

  return (
    <BaseDialog
      message={
        <Typography>
          {translate('app.merchants.merchantWillBeActivated1')}{' '}
          <TypographyBold fontSize={16} text={record?.name || ''} />{' '}
          {translate('app.merchants.merchantWillBeActivated2')}
        </Typography>
      }
      onClose={onClose}
      open={open}
      rightActionsSlot={[
        <Button
          key="close-button"
          label={translate('app.buttons.cancel')}
          onClick={onClose}
          variant="outlined"
        />,
        <Button
          isLoading={isLoading}
          key="archive-button"
          label={translate('app.buttons.restore')}
          onClick={onArchive}
          variant="contained"
        />,
      ]}
      title={translate('app.merchants.restoreMerchant')}
      width="640px"
    />
  );
};
